import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  KPI_LIST,
  KPI_TERRITORY,
  KPI_TERRITORY_AVERAGE_AUTONOMOUS_MODE_RATIO,
  KPI_TERRITORY_AVERAGE_PARTNER_MODE_RATIO,
  KPI_TERRITORY_AVERAGE_SPEED,
} from '../../../constants.ts';
import { useLiveStatistics } from '../../../services/Statistics.ts';
import { DailyKpi } from '../../../types/kpi.ts';
import { getTextColor } from '../../../utils/kpiUtils.ts';
import { CustomSpinner } from '../../ui/Spinner.tsx';
import { OverlayTooltip } from '../../ui/Tooltip.tsx';

export function Kpi() {
  const { t } = useTranslation();
  const params = useParams() as { territorySlug: string };
  const { data: statisticsData, isLoading } = useLiveStatistics(params.territorySlug);
  const [todayData, setTodayData] = useState();
  const [yesterdayData, setYesterdayData] = useState();
  const [deltaData, setDeltaData] = useState<Record<string, number> | undefined>();
  function computeDeltaData(todayData: DailyKpi | undefined, yesterdayData: DailyKpi | undefined) {
    if (todayData !== undefined && yesterdayData !== undefined) {
      const response: Record<string, number> = {};
      for (const kpi of KPI_LIST) {
        const todayValue = todayData[kpi];
        const yesterdayValue = yesterdayData[kpi];
        if (
          typeof todayValue === 'number' &&
          typeof yesterdayValue === 'number' &&
          yesterdayValue !== 0
        ) {
          response[kpi] = Math.round(((todayValue - yesterdayValue) / yesterdayValue) * 100);
        }
      }
      return response;
    }
  }

  useEffect(() => {
    if (statisticsData?.data !== undefined) {
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      const todayKpi = statisticsData?.data?.[KPI_TERRITORY].find((kpi: DailyKpi) =>
        kpi.day.startsWith(today),
      );
      const yesterdayKpi = statisticsData?.data?.[KPI_TERRITORY].find((kpi: DailyKpi) =>
        kpi.day.startsWith(yesterdayStr),
      );
      setTodayData(todayKpi || undefined);
      setYesterdayData(yesterdayKpi || undefined);
      setDeltaData(computeDeltaData(todayKpi, yesterdayKpi));
    }
  }, [statisticsData]);

  return (
    <div className={'row w-100 m-0 my-auto p-0'}>
      {isLoading ? (
        <CustomSpinner class={'text-center'} />
      ) : (
        <>
          <div className={'col-4 my-auto text-center'}>
            <p className={'card-title'}>
              {todayData ? Math.round(todayData[KPI_TERRITORY_AVERAGE_PARTNER_MODE_RATIO] * 100) : '-'} %
            </p>
            <p className={'text-basic'}>
              {t('kpi.partner-mode')}
              <OverlayTooltip text={t('kpi.partner-mode-description')}>
                <img
                  src={'/theme/media/info.svg'}
                  width="15"
                  height="15"
                  className={'ms-1 ariane'}
                  alt="Info icon"
                />
              </OverlayTooltip>
            </p>
            {deltaData && (
              <p className={'text-basic'}>
                <span className={getTextColor(deltaData[KPI_TERRITORY_AVERAGE_PARTNER_MODE_RATIO])}>
                  {deltaData[KPI_TERRITORY_AVERAGE_PARTNER_MODE_RATIO]} %
                </span>{' '}
                vs previous day :{' '}
                {yesterdayData
                  ? Math.round(yesterdayData[KPI_TERRITORY_AVERAGE_PARTNER_MODE_RATIO] * 100)
                  : '-'}{' '}
                %
              </p>
            )}
          </div>
          <div className={'col-4 my-auto text-center'}>
            <p className={'card-title'}>
              {todayData ? Math.round(todayData[KPI_TERRITORY_AVERAGE_SPEED]) : '-'} km/h
            </p>
            <p className={'text-basic'}>
              {t('kpi.average-speed')}
              <OverlayTooltip text={t('kpi.average-speed-description')}>
                <img
                  src={'/theme/media/info.svg'}
                  width="15"
                  height="15"
                  className={'ms-1 ariane'}
                  alt="Info icon"
                />
              </OverlayTooltip>
            </p>
            {deltaData && (
              <p className={'text-basic'}>
                <span className={getTextColor(deltaData[KPI_TERRITORY_AVERAGE_SPEED])}>
                  {deltaData[KPI_TERRITORY_AVERAGE_SPEED]} %
                </span>{' '}
                vs previous day :{' '}
                {yesterdayData ? Math.round(yesterdayData[KPI_TERRITORY_AVERAGE_SPEED]) : '-'} km/h
              </p>
            )}
          </div>
          <div className={'col-4 my-auto text-center'}>
            <p className={'card-title'}>
              {todayData
                ? Math.round(todayData[KPI_TERRITORY_AVERAGE_AUTONOMOUS_MODE_RATIO] * 100)
                : '-'}{' '}
              %
            </p>
            <p className={'text-basic'}>
              {t('kpi.autonomous-mode')}
              <OverlayTooltip text={t('kpi.autonomous-mode-description')}>
                <img
                  src={'/theme/media/info.svg'}
                  width="15"
                  height="15"
                  className={'ms-1 ariane'}
                  alt="Info icon"
                />
              </OverlayTooltip>
            </p>
            {deltaData && (
              <p className={'text-basic'}>
                <span className={getTextColor(deltaData[KPI_TERRITORY_AVERAGE_AUTONOMOUS_MODE_RATIO])}>
                  {deltaData[KPI_TERRITORY_AVERAGE_AUTONOMOUS_MODE_RATIO]} %
                </span>{' '}
                vs previous day :{' '}
                {yesterdayData
                  ? Math.round(yesterdayData[KPI_TERRITORY_AVERAGE_AUTONOMOUS_MODE_RATIO] * 100)
                  : '-'}{' '}
                %
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
